import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { usePowerDialer } from "@/context/PowerDialerContext";
import {
  useGetCallDispositionsQuery,
  useGetCallQuery,
  useGetTelephonySettingsQuery,
  useMarkLeadDoNotCallMutation,
  useMarkLeadWrongNumberMutation,
  useScheduleLeadCallbackMutation,
  useTryCampaignLeadNumberMutation,
} from "@/store/rest_api";
import CallSummaryBlock from "@/components/calls/CallSummaryBlock";
import { phoneSkipLabel } from "@/util/dialer";
import { calendarDateIn, clockTimeIn, formatCalendarDate } from "@/util/date";
import { formatCallingTime } from "@/util/callingRules";
import { CallDispositionAction } from "@/enum/CallDispositionAction";
import { dispositionShortcutKey, dispositionShortcuts } from "@/util/dispositions";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";
import { PhoneOff, UserX } from "lucide-react";
import { getContactDisplayName } from "@/util/string";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SUMMARY_POLL_MS = 5000;

export function WrapUpDialog() {
  const { wrapUpCall, completeWrapUp, dismissWrapUp, setNotes: saveCallNotes, campaign } = usePowerDialer();
  const { data: dispositions = [] } = useGetCallDispositionsQuery({});
  const [markDoNotCall, { isLoading: isMarkingDoNotCall }] = useMarkLeadDoNotCallMutation();
  const [markWrongNumber, { isLoading: isMarkingWrongNumber }] = useMarkLeadWrongNumberMutation();
  const [selectedDisposition, setSelectedDisposition] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [createFollowUpTask, setCreateFollowUpTask] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [tryNumber, { isLoading: isChoosingNumber }] = useTryCampaignLeadNumberMutation();
  const [nextNumber, setNextNumber] = useState<string | null>(null);
  const [scheduleCallback, { isLoading: isSchedulingCallback }] = useScheduleLeadCallbackMutation();
  const { data: telephonySettings } = useGetTelephonySettingsQuery();
  const recordedCallId = wrapUpCall?.call?.id;
  const { data: recordedCall } = useGetCallQuery(recordedCallId ?? "", {
    skip: !recordedCallId || !telephonySettings?.callSummariesEnabled,
    pollingInterval: SUMMARY_POLL_MS,
  });
  const callSummary = recordedCall?.recording?.summary;
  const [callbackDate, setCallbackDate] = useState("");
  const [callbackTime, setCallbackTime] = useState("");
  const [scheduledCallback, setScheduledCallback] = useState<string | null>(null);
  const wrapUpSeconds = campaign?.settings?.wrapUpTime ?? 0;
  const chosenDisposition = dispositions.find((disposition) => disposition.name === selectedDisposition);
  const isCallbackDisposition = chosenDisposition?.action === CallDispositionAction.SCHEDULE_CALLBACK;
  const isFollowUpDisposition = chosenDisposition?.requiresFollowup === true;
  const otherPhones = (wrapUpCall?.contact.phones ?? []).filter(
    (phone) => phone.phoneNumberE164 !== wrapUpCall?.phoneNumber && phone.phoneNumber !== wrapUpCall?.phoneNumber,
  );
  useKeyboardShortcuts(
    {
      ...dispositionShortcuts(dispositions, setSelectedDisposition),
      Enter: () => void handleSave(),
      n: () => {
        const callablePhone = otherPhones.find((phone) => !phoneSkipLabel(phone));
        const number = callablePhone?.phoneNumberE164 ?? callablePhone?.phoneNumber;
        if (number && number !== nextNumber) {
          void handleTryNumber(number);
        }
      },
    },
    Boolean(wrapUpCall),
  );

  const handleTryNumber = async (phoneNumber: string) => {
    if (!wrapUpCall || !campaign) {
      return;
    }
    try {
      await tryNumber({ campaign: { id: campaign.id }, campaignContact: { id: wrapUpCall.id }, phoneNumber }).unwrap();
      setNextNumber(phoneNumber);
      toast.success(`The dialer calls ${phoneNumber} next`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not choose that number"));
    }
  };

  const handleScheduleCallback = async () => {
    if (!wrapUpCall || !campaign || !callbackDate || !callbackTime) {
      return;
    }
    try {
      await scheduleCallback({
        campaign: { id: campaign.id },
        campaignContact: { id: wrapUpCall.id },
        callback: { localDate: callbackDate, localTime: callbackTime },
      }).unwrap();
      setScheduledCallback(`${formatCalendarDate(callbackDate)} at ${formatCallingTime(callbackTime)}`);
      toast.success("Callback set. The lead comes back to you at that time.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not set the callback"));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await completeWrapUp(selectedDisposition, notes, createFollowUpTask && !isFollowUpDisposition);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDoNotCall = async () => {
    if (!wrapUpCall || !campaign) {
      return;
    }
    try {
      await markDoNotCall({ campaign: { id: campaign.id }, campaignContact: { id: wrapUpCall.id } }).unwrap();
      toast.success("Added to the Do Not Call list");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not add this number to the Do Not Call list"));
    }
  };

  const handleWrongNumber = async () => {
    if (!wrapUpCall || !campaign) {
      return;
    }
    try {
      await markWrongNumber({ campaign: { id: campaign.id }, campaignContact: { id: wrapUpCall.id } }).unwrap();
      toast.success("Marked as a wrong number");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not mark this number"));
    }
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (wrapUpCall) {
      saveCallNotes({ id: wrapUpCall.id }, value);
    }
  };

  useEffect(() => {
    if (wrapUpCall) {
      setSelectedDisposition(wrapUpCall.disposition ?? null);
      setNotes(wrapUpCall.notes ?? "");
      setCreateFollowUpTask(false);
      setNextNumber(null);
      setCallbackDate(calendarDateIn(wrapUpCall.leadTimeZone));
      setCallbackTime("");
      setScheduledCallback(null);
    }
  }, [wrapUpCall?.id]);

  useEffect(() => {
    if (!wrapUpCall || wrapUpSeconds <= 0) {
      setSecondsLeft(null);
      return;
    }
    const deadline = Date.now() + wrapUpSeconds * 1000;
    setSecondsLeft(wrapUpSeconds);
    const countdown = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(countdown);
  }, [wrapUpCall?.id, wrapUpSeconds]);

  useEffect(() => {
    if (secondsLeft === 0 && !isSaving) {
      void handleSave();
    }
  }, [secondsLeft]);

  if (!wrapUpCall) {
    return null;
  }

  const displayName = getContactDisplayName(wrapUpCall.contact) ?? wrapUpCall.phoneNumber;

  return (
    <Dialog open onOpenChange={(open) => !open && dismissWrapUp()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Call ended</DialogTitle>
          <DialogDescription>How did the call with {displayName} go?</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-1.5">
            {dispositions.map((disposition, index) => (
              <Button
                key={disposition.id}
                size="sm"
                variant="outline"
                className={cn(
                  "justify-start gap-2 text-xs",
                  selectedDisposition === disposition.name &&
                    "border-primary bg-primary/10 text-primary hover:bg-primary/10",
                )}
                onClick={() => setSelectedDisposition(disposition.name)}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: disposition.color }}
                />
                {disposition.name}
                {dispositionShortcutKey(index) && (
                  <kbd className="ml-auto font-normal text-muted-foreground">{dispositionShortcutKey(index)}</kbd>
                )}
              </Button>
            ))}
            {dispositions.length === 0 && (
              <p className="col-span-2 text-xs text-muted-foreground">
                No dispositions configured for this organisation.
              </p>
            )}
          </div>

          <Textarea
            value={notes}
            onChange={(event) => handleNotesChange(event.target.value)}
            placeholder="Call notes"
            rows={3}
            className="text-sm"
          />

          {callSummary && recordedCallId && <CallSummaryBlock callId={recordedCallId} summary={callSummary} />}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDoNotCall} disabled={isMarkingDoNotCall}>
              <PhoneOff className="mr-2 h-4 w-4" />
              Do not call
            </Button>
            <Button variant="outline" size="sm" onClick={handleWrongNumber} disabled={isMarkingWrongNumber}>
              <UserX className="mr-2 h-4 w-4" />
              Wrong number
            </Button>
          </div>

          {otherPhones.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Other numbers</p>
              {otherPhones.map((phone) => {
                const number = phone.phoneNumberE164 ?? phone.phoneNumber ?? "";
                const skipLabel = phoneSkipLabel(phone);
                return (
                  <div key={number} className="flex items-center justify-between gap-3 text-sm">
                    <span>
                      {phone.phoneNumber ?? phone.phoneNumberE164}
                      {skipLabel && <span className="text-muted-foreground"> · {skipLabel}</span>}
                      {nextNumber === number && <span className="text-muted-foreground"> · called next</span>}
                    </span>
                    {!skipLabel && nextNumber !== number && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isChoosingNumber}
                        onClick={() => void handleTryNumber(number)}
                      >
                        Try this number next
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium">Call back at</p>
              <span className="text-xs text-muted-foreground">
                Their time now: {clockTimeIn(wrapUpCall.leadTimeZone)}
              </span>
            </div>
            {scheduledCallback ? (
              <p className="text-sm text-muted-foreground">
                Callback set for {scheduledCallback}, their time. The lead comes back to you then.
              </p>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={callbackDate}
                  onChange={(event) => setCallbackDate(event.target.value)}
                  className="h-8 text-sm"
                  aria-label="Callback date"
                />
                <Input
                  type="time"
                  value={callbackTime}
                  onChange={(event) => setCallbackTime(event.target.value)}
                  className="h-8 w-32 text-sm"
                  aria-label="Callback time"
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!callbackDate || !callbackTime || isSchedulingCallback}
                  onClick={() => void handleScheduleCallback()}
                >
                  Set
                </Button>
              </div>
            )}
            {isCallbackDisposition && !scheduledCallback && (
              <p className="text-xs text-muted-foreground">
                {selectedDisposition} schedules a callback. Without a time, the lead comes back to you this time
                tomorrow.
              </p>
            )}
          </div>

          {isFollowUpDisposition ? (
            <p className="text-xs text-muted-foreground">
              {selectedDisposition} requires follow-up, so a follow-up task due tomorrow is created for you.
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <Checkbox
                id="wrap-up-follow-up"
                checked={createFollowUpTask}
                onCheckedChange={(checked) => setCreateFollowUpTask(checked === true)}
              />
              <Label htmlFor="wrap-up-follow-up" className="text-sm font-normal">
                Create a follow-up task for tomorrow
              </Label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {secondsLeft !== null && (
            <span className="mr-auto self-center text-sm tabular-nums text-muted-foreground">
              Saving and moving on in {secondsLeft} s
            </span>
          )}
          <Button variant="ghost" onClick={dismissWrapUp} disabled={isSaving}>
            Skip
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save and next
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
